import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { AuthenticatedLayout } from '../components/AuthenticatedLayout';
import { AIInsightsCard } from '../components/AIInsightsCard';
import { api } from '../lib/api';
import type { ScanResults, CategoryResult, RiskLevel, SubscriptionInfo } from '@wrm/shared';
import styles from './WebsiteDetailPage.module.css';

const riskLevelLabels: Record<RiskLevel, string> = {
  minimal: 'Low Priority',
  low: 'Monitor',
  moderate: 'Review Recommended',
  high: 'Attention Needed',
};

interface WebsiteDetail {
  id: string;
  url: string;
  currentScore: number | null;
  lastScanAt: string | null;
  latestScanId: string | null;
}

export function WebsiteDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { getToken } = useAuth();
  const [website, setWebsite] = useState<WebsiteDetail | null>(null);
  const [scanResults, setScanResults] = useState<ScanResults | null>(null);
  const [aiRecommendations, setAiRecommendations] = useState<any | null>(null);
  const [latestScanId, setLatestScanId] = useState<string | null>(null);
  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [reportUrl, setReportUrl] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [showOnlyIssues, setShowOnlyIssues] = useState(false);

  const loadData = useCallback(async () => {
    if (!id) return;

    try {
      const token = await getToken();
      if (!token) return;

      const [websitesRes, subRes] = await Promise.all([
        api.getWebsites(token),
        api.getSubscriptionStatus(token),
      ]);

      const site = websitesRes.websites.find(w => w.id === id);
      if (!site) {
        setError('Website not found');
        return;
      }

      setWebsite(site as WebsiteDetail);
      setSubscription(subRes);

      // Fetch the latest scan for this website
      try {
        const latestScan = await api.getLatestScanForWebsite(id, token);
        if (latestScan && latestScan.results) {
          setScanResults(latestScan.results as ScanResults);
          setLatestScanId(latestScan.scanId);
          // Load AI recommendations if present in the scan
          if (latestScan.aiRecommendations) {
            setAiRecommendations(latestScan.aiRecommendations);
          }
        }
      } catch {
        // No scan results available - that's okay
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load website');
    } finally {
      setLoading(false);
    }
  }, [id, getToken]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleScan = async () => {
    if (!website) return;

    setScanning(true);
    setError('');

    try {
      const token = await getToken();
      if (!token) return;

      // Pass websiteId for database update and get results directly
      const result = await api.startScan(website.url, token, website.id);

      // Scan runs synchronously - results are returned directly
      if (result.status === 'complete' && result.results) {
        setScanResults(result.results as ScanResults);
        setLatestScanId(result.scanId);  // Store scanId for report generation
        // Store AI recommendations if present
        if (result.aiRecommendations) {
          setAiRecommendations(result.aiRecommendations);
        }
        // Update website with new score
        setWebsite({
          ...website,
          currentScore: (result.results as ScanResults).overallScore,
          lastScanAt: new Date().toISOString(),
        });
      }

      setScanning(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start scan');
      setScanning(false);
    }
  };

  const handleGenerateReport = async () => {
    if (!scanResults || !subscription || !latestScanId) return;

    if (subscription.tier === 'free') {
      return; // Show upgrade prompt instead
    }

    setGeneratingReport(true);
    setError('');

    try {
      const token = await getToken();
      if (!token) return;

      // Use the scanId from the latest scan
      const { reportUrl: url } = await api.generateReport(latestScanId, token);
      setReportUrl(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate report');
    } finally {
      setGeneratingReport(false);
    }
  };

  const isPaidUser = subscription && subscription.tier !== 'free';

  return (
    <AuthenticatedLayout>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <Link to="/dashboard" className={styles.backLink}>
            ← Back to Dashboard
          </Link>
          {website && (
            <>
              <h1 className={styles.title}>{new URL(website.url).hostname}</h1>
              <p className={styles.subtitle}>{website.url}</p>
            </>
          )}
        </div>
        <div className={styles.actions}>
          <button
            className="btn btn-primary"
            onClick={handleScan}
            disabled={scanning}
          >
            {scanning ? 'Scanning...' : 'Scan Now'}
          </button>
        </div>
      </div>

      {loading && <p className={styles.loading}>Loading...</p>}
      {error && <p className={styles.error}>{error}</p>}

      {!loading && website && (
        <>
          <div className={styles.scoreSection}>
            {website.currentScore !== null && scanResults ? (
              <>
                <div className={styles.scoreWrapper}>
                  <span className={styles.scoreValue}>{scanResults.overallScore}</span>
                  <span className={styles.scoreMax}>/98</span>
                </div>
                <div className={styles.scoreMeta}>
                  <span className={`risk-badge risk-${scanResults.riskLevel}`}>
                    {riskLevelLabels[scanResults.riskLevel]}
                  </span>
                  {website.lastScanAt && (
                    <p className={styles.lastScan}>
                      Last scanned: {new Date(website.lastScanAt).toLocaleDateString()}
                    </p>
                  )}
                </div>
              </>
            ) : (
              <p className={styles.noScan}>No scan results yet. Click "Scan Now" to analyze this website.</p>
            )}
          </div>

          {/* AI Insights - for paid users with AI recommendations */}
          {aiRecommendations && isPaidUser && (
            <AIInsightsCard recommendations={aiRecommendations} />
          )}

          {/* Report Section */}
          <div className={styles.reportSection}>
            <div className={styles.reportHeader}>
              <h2 className={styles.reportTitle}>Report</h2>
            </div>
            <p className={styles.reportDescription}>
              Generate a comprehensive report with detailed findings, recommendations, and executive summary.
            </p>

            {isPaidUser ? (
              <div className={styles.reportActions}>
                <button
                  className="btn btn-primary"
                  onClick={handleGenerateReport}
                  disabled={generatingReport || !scanResults}
                >
                  {generatingReport ? 'Generating...' : 'Generate Report'}
                </button>
                {reportUrl && (
                  <a
                    href={reportUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-secondary"
                  >
                    Download Report
                  </a>
                )}
              </div>
            ) : (
              <div className={styles.upgradePrompt}>
                <p className={styles.upgradeText}>
                  Reports are available on paid plans. Upgrade to access detailed reports with recommendations.
                </p>
                <Link to="/subscription" className="btn btn-primary">
                  Upgrade Plan
                </Link>
              </div>
            )}
          </div>

          {/* Category Breakdown */}
          {scanResults && (
            <>
              <div className={styles.sectionHeader}>
                <h2 className={styles.sectionTitle}>Category Breakdown</h2>
                <button
                  className={`${styles.filterButton} ${showOnlyIssues ? styles.filterActive : ''}`}
                  onClick={() => setShowOnlyIssues(!showOnlyIssues)}
                >
                  {showOnlyIssues ? '✓ Showing Issues Only' : 'Show Issues Only'}
                </button>
              </div>
              <div className={styles.categoriesGrid}>
                {scanResults.categories.map((category) => (
                  <CategoryCard key={category.name} category={category} showOnlyIssues={showOnlyIssues} />
                ))}
              </div>
            </>
          )}
        </>
      )}
    </AuthenticatedLayout>
  );
}

function CategoryCard({ category, showOnlyIssues }: { category: CategoryResult; showOnlyIssues: boolean }) {
  const filteredFindings = showOnlyIssues
    ? category.findings.filter(f => f.status === 'fail' || f.status === 'warning')
    : category.findings;

  // Hide category entirely if showing only issues and no issues exist
  if (showOnlyIssues && filteredFindings.length === 0) {
    return null;
  }

  return (
    <div className={styles.categoryCard}>
      <div className={styles.categoryHeader}>
        <h3 className={styles.categoryName}>
          {category.name.charAt(0).toUpperCase() + category.name.slice(1)}
        </h3>
        <div className={styles.categoryScore}>
          <span className={styles.categoryScoreValue}>{category.score}</span>
          <span className={styles.categoryScoreMax}>/100</span>
        </div>
      </div>

      {/* Progress Bar */}
      <div className={styles.categoryProgress}>
        <div
          className={styles.categoryProgressBar}
          style={{ width: `${category.score}%` }}
        />
      </div>

      <p className={styles.categoryWeight}>Weight: {Math.round(category.weight * 100)}%</p>

      <ul className={styles.findings}>
        {filteredFindings.map((finding) => (
          <li key={finding.id} className={styles.finding}>
            <span className={`${styles.findingStatus} ${styles[finding.status]}`}>
              {finding.status === 'pass' ? '✓' : finding.status === 'fail' ? '✗' : '?'}
            </span>
            <div className={styles.findingContent}>
              <p className={styles.findingTitle}>{finding.title}</p>
              <p className={styles.findingDescription}>{finding.description}</p>
              {finding.status !== 'pass' && finding.recommendation && (
                <p className={styles.findingRecommendation}>
                  {finding.recommendation}
                </p>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
